import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import dayjs from "dayjs";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";

const useStyles = makeStyles({
  card: {
    marginBottom: 12,
  },
  cardContent: {
    paddingBottom: 8,
  },
  header: {
    marginBottom: 12,
  },
  clientName: {
    fontWeight: 600,
    fontSize: 16,
    color: "#006EB3",
  },
  infoGrid: {
    marginTop: 8,
  },
  label: {
    fontSize: 12,
    color: "#666",
    marginBottom: 4,
  },
  value: {
    fontWeight: 500,
    fontSize: 14,
  },
  statusChip: {
    fontWeight: 500,
    fontSize: 12,
  },
  lmsContainer: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  cardActions: {
    padding: "8px 16px",
    paddingTop: 0,
  },
});

interface UserClientCardProps { client: { email?: React.ReactNode; first_name?: string; hasAssessments?: boolean; hasPending?: boolean; last_name?: string; lms_accessed?: boolean; start_date?: string; [key: string]: React.ReactNode | boolean | undefined }; onView?: () => void; }

export default function UserClientCard({ client, onView }: UserClientCardProps): React.ReactElement {
  const classes = useStyles();

  const getStatusChip = () => {
    if (!client.hasAssessments) {
      return (
        <Chip
          label="None Assigned"
          className={classes.statusChip}
          style={{ color: "#b94946", borderColor: "#b94946" }}
          variant="outlined"
          size="small"
        />
      );
    } else if (client.hasPending) {
      return (
        <Chip
          label="Pending"
          className={classes.statusChip}
          style={{ background: "#fcb316", color: "#fff" }}
          size="small"
        />
      );
    } else {
      return (
        <Chip
          label="All Completed"
          className={classes.statusChip}
          style={{ background: "#03aab1", color: "#fff" }}
          size="small"
        />
      );
    }
  };

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <Typography className={classes.clientName}>
            {client.first_name} {client.last_name}
          </Typography>
        </div>

        <Grid container spacing={2} className={classes.infoGrid}>
          <Grid item xs={12}>
            <Typography className={classes.label}>Email</Typography>
            <Typography className={classes.value}>{client.email}</Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography className={classes.label}>Created</Typography>
            <Typography className={classes.value}>
              {dayjs(client.start_date).format("DD/MM/YYYY")}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography className={classes.label}>LMS Accessed</Typography>
            <div className={classes.lmsContainer}>
              {!client.lms_accessed ? (
                <CancelIcon style={{ color: "#c73c3c", fontSize: 20 }} />
              ) : (
                <CheckCircleIcon style={{ color: "#49a87b", fontSize: 20 }} />
              )}
              <Typography className={classes.value}>
                {client.lms_accessed ? "Yes" : "No"}
              </Typography>
            </div>
          </Grid>

          <Grid item xs={12}>
            <Typography className={classes.label}>
              Assignment Status
            </Typography>
            {getStatusChip()}
          </Grid>
        </Grid>
      </CardContent>

      <CardActions className={classes.cardActions}>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          disabled={!client.hasAssessments}
          onClick={onView}
        >
          View Assignments
        </Button>
      </CardActions>
    </Card>
  );
}
